import { process, State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';
import {
  Dispatch,
  FC,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IsSupplierUser } from 'src/constants/permissionsList';
import { queryKeys } from 'src/constants/querykeys';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage, { handlePostMessage } from 'src/hooks/usePostMessage';
import useRedirect from 'src/hooks/useRedirect';
import useCreateCartPoMutation from 'src/services/cart/useCreateCartPoMutation';
import useDeleteCartMutation from 'src/services/cart/useDeleteCartMutation';
import useGetShoppingCartQuery from 'src/services/cart/useGetShoppingCartQuery';
import useUpdateCartQuantitiesMutation from 'src/services/cart/useUpdateCartQuantitiesMutation';
import { fetchProductDetailsQuery } from 'src/services/catalog_products/productDetailsQueryFn';
import useGetProductDetailsQuery from 'src/services/catalog_products/useGetProductDetailsQuery';
import useAddToFavoritesMutation from 'src/services/favorite/useAddToFavoritesMutation';
import useGetAllFavoriteFoldersQuery from 'src/services/favorite/useGetAllFavoriteFoldersQuery';
import {
  buildOfflineSupplierFilterRequest,
  buildOnlineSupplierFilterRequest,
} from 'src/services/manage_suppliers/buildSupplierFilterRequest';
import useSuppliersQuery from 'src/services/manage_suppliers/useSuppliersQuery';
import { ShoppingCartSupplierListItem } from 'src/types/cart/shoppingCart';
import { UpdateCartQuantityItem } from 'src/types/cart/updateQuantities';
import { ProductDetails } from 'src/types/catalog/ProductDetails';
import { FavoriteProduct } from 'src/types/favorite';
import { SetState } from 'src/types/generic';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { RecommendPageParams } from 'src/types/recommendation_items';
import {
  ShoppingCartProduct,
  ShoppingCartProductSelection,
  ShoppingCartProductSelections,
  ShoppingCartSelectedGridTotals,
  ShoppingCartSupplierCardSummary,
} from 'src/types/shopping_cart/ShoppingCart';
import { ShoppingListOption } from 'src/types/shopping_lists/ShoppingList';
import { createContext } from 'use-context-selector';
import {
  applyShoppingCartAttributesFilter,
  applyShoppingCartHeaderSelection,
  applyShoppingCartSearchTextFilter,
  applyShoppingCartSupplierFilter,
  buildShoppingCartProcessState,
  computeSelectedShoppingCartGridTotals,
  extractAttributesFilter,
  getProductsForSupplier,
  getShoppingCartProductSelection,
  getVisibleShoppingCartSelections,
  isShoppingCartSupplierSelected,
  mapShoppingCartApiItem,
  mapShoppingCartProductToCreatePoCartItem,
  prepareShoppingCartProductsForGrid,
  resolveCartItemSupplierCompanyId,
  resolveQtyOnProductSelection,
} from './shoppingCartProductMapper';

const CREATE_PO_CONFIRM_DIALOG_WIDTH = 'min(450px, calc(100vw - 32px))';

export const initialShoppingCartDataState: State = {
  skip: 0,
  take: 500,
  sort: [],
  filter: { logic: 'and', filters: [] },
};

export interface ShoppingCartContextProps {
  filterSearch: string;
  handleFilterSearchChange: (value: string) => void;
  dataState: State;
  gridDataState: State;
  setDataState: Dispatch<SetStateAction<State>>;
  onDataStateChange: (event: GridDataStateChangeEvent) => void;
  productSelections: ShoppingCartProductSelections;
  setProductSelections: Dispatch<SetStateAction<ShoppingCartProductSelections>>;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  onItemChange: (event: GridItemChangeEvent) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  hasSelectedItems: boolean;
  hasSelectedProductsForPo: boolean;
  selectedGridTotals: ShoppingCartSelectedGridTotals;
  allProducts: ShoppingCartProduct[];
  products: ShoppingCartProduct[];
  productsTotal: number;
  loadingProducts: boolean;
  suppliers: ShoppingCartSupplierListItem[];
  supplierCardSummaries: ShoppingCartSupplierCardSummary[];
  selectedSupplierIds: string[];
  toggleSupplierSelection: (id: string, selected: boolean) => void;
  handleDeleteSelected: () => Promise<void>;
  isDeletingCartItems: boolean;
  saveQuantity: (productKey: string, qty: number) => void;
  hasUnsavedQuantityChanges: boolean;
  handleCreatePoForSelected: () => Promise<void>;
  isCreatePoActionInProgress: boolean;
  handleReviewContractedAlternatives: () => void;
  hasNotRecommendedCartItems: boolean;
  handleCloseSwapItemPanel: () => void;
  selectedSupplierForInfo: ShoppingCartSupplierListItem | null;
  openSupplierInfo: (supplier: ShoppingCartSupplierListItem) => void;
  closeSupplierInfo: () => void;
  supplierForInfo: ManageSupplier | null;
  isLoadingSupplierInfo: boolean;
  isErrorSupplierInfo: boolean;
  isSwapItemModalOpen: boolean;
  setIsSwapItemModalOpen: SetState<boolean>;
  swapItemData: RecommendPageParams | null;
  setSwapItemData: SetState<RecommendPageParams | null>;
  handleViewProductDetails: (e: MouseEvent, product: ShoppingCartProduct) => Promise<void>;
  isProductPanelOpen: boolean;
  handleCloseProductPanel: () => void;
  selectedProduct: ShoppingCartProduct | null;
  setSelectedProduct: SetState<ShoppingCartProduct | null>;
  productDetails: ProductDetails | undefined;
  loadingProductDetails: boolean;
  isFetchingProductDetails: boolean;
  updateProductSelection: (
    productKey: string,
    updates: Partial<ShoppingCartProductSelection>
  ) => void;
  handleAddToFavorites: (folderId: number) => Promise<void>;
  folderOptions: ShoppingListOption[];
  isAddingToFavorites: boolean;
  isSupplierUser: boolean;
  fetchProductDetails: () => Promise<QueryObserverResult<ProductDetails | undefined>>;
  fetchProducts: () => Promise<unknown>;
  getSelectedProductCatalogType: () => number | undefined;
}

export const ShoppingCartContext = createContext<ShoppingCartContextProps>(
  {} as ShoppingCartContextProps
);

export const ShoppingCartContextProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { getUrl, redirect } = useRedirect();
  const queryClient = useQueryClient();
  const isSupplierUser = usePermission({ permissions: IsSupplierUser });
  const {
    data: shoppingCart,
    isLoading,
    isFetching,
    refetch: refetchShoppingCart,
  } = useGetShoppingCartQuery();
  const { data: productListOptions = [] } = useGetAllFavoriteFoldersQuery();
  const { mutateAsync: deleteCartItems, isPending: isDeletingCartItems } = useDeleteCartMutation();
  const { mutateAsync: updateCartQuantities } = useUpdateCartQuantitiesMutation();
  const { mutateAsync: createCartPo } = useCreateCartPoMutation();
  const { mutateAsync: addToFavorites, isPending: isAddingToFavorites } = useAddToFavoritesMutation(
    {}
  );
  const [filterSearch, setFilterSearch] = useState('');
  const [dataState, setDataState] = useState<State>({ ...initialShoppingCartDataState });
  const [productSelections, setProductSelections] = useState<ShoppingCartProductSelections>({});
  const [visibleSupplierIds, setVisibleSupplierIds] = useState<Set<string>>(new Set());
  const [selectedSupplierForInfo, setSelectedSupplierForInfo] =
    useState<ShoppingCartSupplierListItem | null>(null);
  const isOfflineSupplierForInfo = selectedSupplierForInfo?.isOfflineSupplier ?? false;
  const supplierInfoId = selectedSupplierForInfo?.supplierClientId ?? null;
  const supplierFilterRequest = useMemo(() => {
    if (supplierInfoId == null) {
      return buildOnlineSupplierFilterRequest(0);
    }

    return isOfflineSupplierForInfo
      ? buildOfflineSupplierFilterRequest(supplierInfoId)
      : buildOnlineSupplierFilterRequest(supplierInfoId);
  }, [isOfflineSupplierForInfo, supplierInfoId]);
  const {
    data: supplierForInfoData,
    isLoading: isLoadingSupplierForInfo,
    isFetching: isFetchingSupplierForInfo,
    isError: isErrorSupplierForInfo,
  } = useSuppliersQuery({
    request: supplierFilterRequest,
    enabled: selectedSupplierForInfo != null && supplierInfoId != null,
  });
  const supplierForInfo = supplierForInfoData?.items?.[0] ?? null;
  const isLoadingSupplierInfo =
    selectedSupplierForInfo != null &&
    (isLoadingSupplierForInfo || isFetchingSupplierForInfo);
  const isErrorSupplierInfo = isErrorSupplierForInfo;
  const [isSwapItemModalOpen, setIsSwapItemModalOpen] = useState(false);
  const [swapItemData, setSwapItemData] = useState<RecommendPageParams | null>(null);
  const [isProductPanelOpen, setIsProductPanelOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ShoppingCartProduct | null>(null);
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState(false);
  const [isCreatePoActionInProgress, setIsCreatePoActionInProgress] = useState(false);
  const isFetchingProductDetailsRef = useRef(false);
  // Qty saved on blur but not yet reflected in the refetched cart, keyed by clientOrderBinId
  const pendingQtyByOrderBinRef = useRef<Map<number, number>>(new Map());
  const pendingQuantitySavesRef = useRef<Set<Promise<boolean>>>(new Set());
  // Each line's most recent save, so the next one for that line waits for it
  const lastQuantitySaveByOrderBinRef = useRef<Map<number, Promise<boolean>>>(new Map());
  // Blur saves refetch the cart in the background, so the grid loader stays hidden while they run
  const [activeQuantitySaveCount, setActiveQuantitySaveCount] = useState(0);

  const folderOptions = useMemo(
    () =>
      productListOptions.map((folder) => ({
        id: folder.id,
        name: folder.description,
      })),
    [productListOptions]
  );

  const supplierCompanyIdByClientId = useMemo(() => {
    const map = new Map<number, number>();
    shoppingCart?.supplierList?.forEach((supplier) => {
      map.set(supplier.supplierClientId, supplier.supplierCompanyId);
    });
    return map;
  }, [shoppingCart?.supplierList]);

  const allProducts = useMemo<ShoppingCartProduct[]>(() => {
    if (!shoppingCart?.cartItems?.length) {
      return [];
    }

    return shoppingCart.cartItems.map((item) =>
      mapShoppingCartApiItem(
        item,
        resolveCartItemSupplierCompanyId(item, supplierCompanyIdByClientId)
      )
    );
  }, [shoppingCart?.cartItems, supplierCompanyIdByClientId]);

  const hasNotRecommendedCartItems = useMemo(
    () => allProducts.some((product) => product.isNoItem),
    [allProducts]
  );
  useEffect(() => {
    if (!allProducts.length) {
      setProductSelections({});
      return;
    }

    setProductSelections((prev) => {
      const next = { ...prev };
      let changed = false;

      allProducts.forEach((product) => {
        const existing = next[product.productKey];
        if (!existing) {
          next[product.productKey] = {
            selected: product.quantity !== 0,
            qty: product.quantity,
          };
          changed = true;
          return;
        }

        const { qty: existingQty, selected: existingSelected } = existing;
        let qty = existingQty;
        let selected = existingSelected;
        let productChanged = false;

        if (existingQty === 0 && product.quantity !== 0) {
          qty = product.quantity;
          productChanged = true;
        }

        const effectiveQty = qty ?? product.quantity;
        if (effectiveQty === 0 && selected) {
          selected = false;
          productChanged = true;
        }

        if (productChanged) {
          next[product.productKey] = { selected, qty: effectiveQty };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [allProducts]);

  const searchFilteredProducts = useMemo(
    () => applyShoppingCartSearchTextFilter(allProducts, filterSearch),
    [allProducts, filterSearch]
  );

  const attributesFilter = useMemo(
    () => extractAttributesFilter(dataState.filter),
    [dataState.filter]
  );

  const gridFilterableProducts = useMemo(
    () => applyShoppingCartAttributesFilter(searchFilteredProducts, attributesFilter),
    [searchFilteredProducts, attributesFilter]
  );

  const suppliers = useMemo(() => shoppingCart?.supplierList ?? [], [shoppingCart?.supplierList]);

  useEffect(() => {
    const supplierIds = suppliers.map((supplier) => String(supplier.supplierClientId));

    setVisibleSupplierIds((prev) => {
      if (!supplierIds.length) {
        return prev.size === 0 ? prev : new Set();
      }

      if (prev.size === 0) {
        return new Set(supplierIds);
      }

      const next = new Set(prev);
      let changed = false;

      supplierIds.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });

      for (const id of next) {
        if (!supplierIds.includes(id)) {
          next.delete(id);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [suppliers]);

  const selectedSupplierIds = useMemo(() => Array.from(visibleSupplierIds), [visibleSupplierIds]);

  const processDataState = useMemo(() => buildShoppingCartProcessState(dataState), [dataState]);

  const getQtyForProduct = useCallback(
    (productKey: string, defaultQty: number) => {
      const selection = productSelections[productKey];
      return selection?.qty ?? defaultQty;
    },
    [productSelections]
  );

  const supplierFilteredProducts = useMemo(
    () => applyShoppingCartSupplierFilter(gridFilterableProducts, suppliers, selectedSupplierIds),
    [gridFilterableProducts, suppliers, selectedSupplierIds]
  );

  const gridProducts = useMemo(
    () => prepareShoppingCartProductsForGrid(supplierFilteredProducts, getQtyForProduct),
    [supplierFilteredProducts, getQtyForProduct]
  );

  // Rows that pass the search box and grid filters, across all pages
  const filteredProductKeys = useMemo(
    () =>
      new Set(
        (
          process(gridProducts, { filter: processDataState.filter }).data as ShoppingCartProduct[]
        ).map((product) => product.productKey)
      ),
    [gridProducts, processDataState.filter]
  );

  // What the user can act on: rows hidden by a filter count as deselected until it is cleared
  const visibleProductSelections = useMemo(
    () => getVisibleShoppingCartSelections(productSelections, filteredProductKeys),
    [filteredProductKeys, productSelections]
  );

  const supplierCardSummaries = useMemo<ShoppingCartSupplierCardSummary[]>(
    () =>
      suppliers
        .map((supplier) => ({
          supplier,
          selected: isShoppingCartSupplierSelected(allProducts, supplier, visibleProductSelections),
        }))
        .sort((a, b) =>
          (a.supplier.supplierCompanyName ?? '').localeCompare(
            b.supplier.supplierCompanyName ?? '',
            undefined,
            { sensitivity: 'base' }
          )
        ),
    [allProducts, visibleProductSelections, suppliers]
  );

  const selectedGridTotals = useMemo(
    () => computeSelectedShoppingCartGridTotals(allProducts, visibleProductSelections),
    [allProducts, visibleProductSelections]
  );

  const processedProducts = useMemo(
    () => process(gridProducts, processDataState),
    [gridProducts, processDataState]
  );

  const products = processedProducts.data as ShoppingCartProduct[];
  const productsTotal = processedProducts.total;
  const loadingProducts =
    isLoading || (isFetching && activeQuantitySaveCount === 0) || isLoadingSupplierInfo;

  const {
    data: apiProductDetails,
    isLoading: isLoadingProductDetails,
    isFetching: fetchingProductDetails,
    refetch: fetchProductDetails,
  } = useGetProductDetailsQuery(
    get(selectedProduct, 'productId', null),
    get(selectedProduct, 'catalogId', null),
    get(selectedProduct, 'categoryId', null)
  );

  const productDetails = apiProductDetails;
  const loadingProductDetails =
    isLoadingProductDetails || fetchingProductDetails || isFetchingProductDetails;

  const hasActiveFilters = Boolean(
    filterSearch.trim() ||
      dataState.filter?.filters?.length ||
      (dataState.sort && dataState.sort.length > 0)
  );

  const hasSelectedItems = useMemo(
    () => allProducts.some((product) => visibleProductSelections[product.productKey]?.selected),
    [allProducts, visibleProductSelections]
  );

  const hasUnsavedQuantityChanges = useMemo(
    () =>
      allProducts.some((product) => {
        const selection = productSelections[product.productKey];
        const currentQty = selection?.qty ?? product.quantity;
        return currentQty !== product.quantity;
      }),
    [allProducts, productSelections]
  );

  const getChangedCartQuantityItems = useCallback(
    () =>
      allProducts.reduce<UpdateCartQuantityItem[]>((items, product) => {
        const selection = productSelections[product.productKey];
        const currentQty = selection?.qty ?? product.quantity;

        if (currentQty !== product.quantity) {
          items.push({
            clientOrderBinId: product.clientOrderBinId,
            quantity: currentQty,
          });
        }

        return items;
      }, []),
    [allProducts, productSelections]
  );

  const getSelectedCartProducts = useCallback(
    () => allProducts.filter((product) => visibleProductSelections[product.productKey]?.selected),
    [allProducts, visibleProductSelections]
  );

  const getSelectedCartProductsForPo = useCallback(
    () =>
      getSelectedCartProducts().filter((product) => {
        const selection = productSelections[product.productKey];
        const qty = selection?.qty ?? product.quantity;

        return qty !== 0;
      }),
    [getSelectedCartProducts, productSelections]
  );

  const selectAllProductsWithQuantity = useCallback(() => {
    setProductSelections((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.entries(prev).forEach(([productKey, selection]) => {
        const shouldSelect = selection.qty !== 0;
        if (selection.selected !== shouldSelect) {
          next[productKey] = {
            ...selection,
            selected: shouldSelect,
          };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, []);

  const hasSelectedProductsForPo = useMemo(
    () => getSelectedCartProductsForPo().length > 0,
    [getSelectedCartProductsForPo]
  );

  useEffect(() => {
    setDataState((prev) => ({ ...prev, skip: 0 }));
  }, [filterSearch]);

  const handleFilterSearchChange = useCallback((value: string) => {
    setFilterSearch(value);
    setDataState((prev) => ({ ...prev, skip: 0 }));
  }, []);

  const onDataStateChange = useCallback((event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  }, []);

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const { dataItem } = event;
      const productKey = String(dataItem?.productKey ?? '');
      if (!productKey) {
        return;
      }

      const product = allProducts.find((item) => item.productKey === productKey);

      setProductSelections((prev) => {
        const current = getShoppingCartProductSelection(prev, productKey, product?.quantity ?? 1);

        const newSelected = !current.selected;
        const qty = resolveQtyOnProductSelection(newSelected, current.qty);

        return {
          ...prev,
          [productKey]: {
            selected: newSelected,
            qty,
          },
        };
      });
    },
    [allProducts]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event?.syntheticEvent?.target as HTMLInputElement;
      const { checked } = checkboxElement;

      setProductSelections((prev) =>
        applyShoppingCartHeaderSelection(prev, products, allProducts, checked)
      );
    },
    [allProducts, products]
  );

  const onItemChange = useCallback(
    (event: GridItemChangeEvent) => {
      const productKey = String(event.dataItem?.productKey ?? '');
      if (!productKey || event.field !== 'qty') {
        return;
      }

      const product = allProducts.find((item) => item.productKey === productKey);

      setProductSelections((prev) => {
        const current = getShoppingCartProductSelection(prev, productKey, product?.quantity ?? 1);
        const qty = Number(event.value) || 0;

        return {
          ...prev,
          [productKey]: {
            ...current,
            qty,
            selected: qty !== 0,
          },
        };
      });
    },
    [allProducts]
  );

  const clearFilters = useCallback(() => {
    setFilterSearch('');
    setDataState((prev) => ({
      ...prev,
      skip: 0,
      filter: { logic: 'and', filters: [] },
      sort: [],
    }));
  }, []);

  const toggleSupplierSelection = useCallback(
    (id: string, selected: boolean) => {
      const supplier = suppliers.find((item) => String(item.supplierClientId) === id);
      if (!supplier) {
        return;
      }

      setVisibleSupplierIds((prev) => {
        const next = new Set(prev);

        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }

        return next;
      });

      const supplierProducts = getProductsForSupplier(allProducts, supplier);

      setProductSelections((prev) => {
        const next = { ...prev };

        supplierProducts.forEach((product) => {
          const current = getShoppingCartProductSelection(
            prev,
            product.productKey,
            product.quantity
          );

          next[product.productKey] = {
            selected,
            qty: resolveQtyOnProductSelection(selected, current.qty),
          };
        });

        return next;
      });
    },
    [allProducts, suppliers]
  );

  const saveCartQuantities = useCallback(
    async (
      items: UpdateCartQuantityItem[],
      refetchShoppingCartApi: boolean = true
    ): Promise<boolean> => {
      try {
        if (items.length === 0) {
          return true;
        }

        await updateCartQuantities({ items });
        handlePostMessage({ cartUpdated: true });
        if (refetchShoppingCartApi) {
          await refetchShoppingCart();
        }
        // No success toast: quantities autosave on blur, so a toast per edit is noise.
        return true;
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          t('Unable to update cart quantities. Please try again.');
        toast.error(errorMessage);
        return false;
      }
    },
    [refetchShoppingCart, t, updateCartQuantities]
  );

  const saveQuantity = useCallback(
    (productKey: string, qty: number) => {
      const product = allProducts.find((item) => item.productKey === productKey);
      if (!product) {
        return;
      }

      setProductSelections((prev) => {
        const current = getShoppingCartProductSelection(prev, productKey, product.quantity);
        if (current.qty === qty) {
          return prev;
        }

        return {
          ...prev,
          [productKey]: { qty, selected: qty !== 0 },
        };
      });

      const { clientOrderBinId } = product;
      const pendingQtyByOrderBin = pendingQtyByOrderBinRef.current;
      const lastRequestedQty = pendingQtyByOrderBin.get(clientOrderBinId) ?? product.quantity;
      if (qty === lastRequestedQty) {
        return;
      }

      pendingQtyByOrderBin.set(clientOrderBinId, qty);
      setActiveQuantitySaveCount((count) => count + 1);
      const pendingSaves = pendingQuantitySavesRef.current;
      // Auto-save can start a second save for a line while its first is in flight; queue it
      // behind that one so the server always ends on the latest qty.
      const lastSaveByOrderBin = lastQuantitySaveByOrderBinRef.current;
      const previousSave = lastSaveByOrderBin.get(clientOrderBinId) ?? Promise.resolve(true);
      const save = previousSave
        .then(() => saveCartQuantities([{ clientOrderBinId, quantity: qty }]))
        .finally(() => {
          if (pendingQtyByOrderBin.get(clientOrderBinId) === qty) {
            pendingQtyByOrderBin.delete(clientOrderBinId);
          }
          if (lastSaveByOrderBin.get(clientOrderBinId) === save) {
            lastSaveByOrderBin.delete(clientOrderBinId);
          }
          pendingSaves.delete(save);
          // TanStack Query publishes isFetching: false on a setTimeout(0), so release the
          // counter after it; otherwise the grid loader flashes for one render.
          setTimeout(() => setActiveQuantitySaveCount((count) => count - 1), 0);
        });
      lastSaveByOrderBin.set(clientOrderBinId, save);
      pendingSaves.add(save);
    },
    [allProducts, saveCartQuantities]
  );

  const handleCreatePoForSelected = useCallback(async () => {
    try {
      const selectedProducts = getSelectedCartProductsForPo();

      if (selectedProducts.length === 0) {
        toast.info(t('Quantity cannot be zero'));
        return;
      }

      // Blur saves already in flight are awaited below, so only the rest still need saving
      const pendingQuantitySaves = Array.from(pendingQuantitySavesRef.current);
      const unsavedQuantityItems = getChangedCartQuantityItems().filter(
        (item) => pendingQtyByOrderBinRef.current.get(item.clientOrderBinId) !== item.quantity
      );
      const hasUnsavedQuantityItems = unsavedQuantityItems.length > 0;

      const isAccepted = await confirm(
        hasUnsavedQuantityItems
          ? {
              title: t('Confirm Save'),
              content: t('Save before creating the PO?'),
              confirmButtonText: t('OK'),
              cancelButtonText: t('Cancel'),
              width: CREATE_PO_CONFIRM_DIALOG_WIDTH,
            }
          : {
              title: t('Confirm PO Creation'),
              content: t('Create PO for the selected item(s)?'),
              confirmButtonText: t('OK'),
              cancelButtonText: t('Cancel'),
              width: CREATE_PO_CONFIRM_DIALOG_WIDTH,
            }
      );

      if (!isAccepted) {
        return;
      }

      setIsCreatePoActionInProgress(true);

      const pendingSaveResults = await Promise.all(pendingQuantitySaves);
      if (pendingSaveResults.includes(false)) {
        return;
      }

      if (hasUnsavedQuantityItems) {
        const isSaved = await saveCartQuantities(unsavedQuantityItems, false);
        if (!isSaved) {
          return;
        }
      }

      const poIds = await createCartPo({
        cartItems: selectedProducts.map(mapShoppingCartProductToCreatePoCartItem),
      });
      if (!poIds.length) {
        toast.error(t('Unable to create the purchase order. Please try again.'));
        return;
      }

      handlePostMessage({ cartUpdated: true });
      await refetchShoppingCart();
      selectAllProductsWithQuantity();

      const [firstPoId, ...remainingPoIds] = poIds;

      remainingPoIds.forEach((poId) => {
        redirect(getUrl('poDetails')(String(poId)), true, true);
      });

      redirect(getUrl('poDetails')(String(firstPoId)), true, false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        t('Unable to create the purchase order. Please try again.');
      toast.error(errorMessage);
    } finally {
      setIsCreatePoActionInProgress(false);
    }
  }, [
    confirm,
    createCartPo,
    getChangedCartQuantityItems,
    getSelectedCartProductsForPo,
    getUrl,
    redirect,
    refetchShoppingCart,
    saveCartQuantities,
    selectAllProductsWithQuantity,
    t,
  ]);

  const handleReviewContractedAlternatives = useCallback(() => {
    if (!hasNotRecommendedCartItems) {
      toast.info(t('contractedAlternativesNotFound'));
      return;
    }

    setSwapItemData({
      pageType: 1,
      isBulkRequest: true,
      supplierId: 0,
      supplierCompanyId: 0,
      itemNumber: '',
      productId: 0,
      catalogId: 0,
      categoryId: 0,
      quantity: 0,
      price: 0,
      uom: '',
    });
    setIsSwapItemModalOpen(true);
  }, [hasNotRecommendedCartItems, t]);

  const handleCloseSwapItemPanel = useCallback(() => {
    setIsSwapItemModalOpen(false);
    setSwapItemData(null);
    // setProductSelections({});
    // void refetchShoppingCart();
  }, []);

  const openSupplierInfo = useCallback((supplier: ShoppingCartSupplierListItem) => {
    setSelectedSupplierForInfo(supplier);
  }, []);

  const closeSupplierInfo = useCallback(() => {
    setSelectedSupplierForInfo(null);
  }, []);

  const handleViewProductDetails = useCallback(
    async (e: MouseEvent, product: ShoppingCartProduct) => {
      e.preventDefault();
      e.stopPropagation();

      if (isFetchingProductDetailsRef.current || loadingProductDetails) {
        return;
      }

      try {
        isFetchingProductDetailsRef.current = true;
        setIsFetchingProductDetails(true);

        await queryClient.cancelQueries({
          queryKey: [...queryKeys.product.root, 'productDetails'],
        });

        setSelectedProduct(product);

        await queryClient.fetchQuery({
          queryKey: queryKeys.product.productDetails(
            product.productId,
            product.catalogId,
            product.categoryId
          ),
          queryFn: fetchProductDetailsQuery,
        });

        setIsProductPanelOpen(true);
      } catch (error) {
        if ((error as Error)?.name !== 'CancelledError') {
          console.error('Failed to fetch product details:', error);
        }
      } finally {
        isFetchingProductDetailsRef.current = false;
        setIsFetchingProductDetails(false);
      }
    },
    [loadingProductDetails, queryClient]
  );

  const handleCloseProductPanel = useCallback(() => {
    setIsProductPanelOpen(false);
    queryClient.removeQueries({ queryKey: ['productDetails'] });
    setSelectedProduct(null);
  }, [queryClient]);

  const handleDeleteSelected = useCallback(async () => {
    try {
      const selectedProducts = getSelectedCartProducts();

      if (selectedProducts.length === 0) {
        toast.error(t('Please select at least one item'));
        return;
      }

      const isDeleteAccepted = await confirm({
        title: t('deleteConfirmationTitle'),
        content: t('Are you sure you want to Delete the selected item(s)?'),
        confirmButtonText: t('yesDelete'),
        cancelButtonText: t('noCancel'),
      });

      if (!isDeleteAccepted) {
        return;
      }

      const clientOrderBinIds = selectedProducts.map((product) => product.clientOrderBinId);

      await deleteCartItems(clientOrderBinIds);
      handlePostMessage({ cartUpdated: true });
      await refetchShoppingCart();

      const deletedProductKeys = new Set(selectedProducts.map((product) => product.productKey));
      setProductSelections((prev) => {
        const next = { ...prev };
        deletedProductKeys.forEach((productKey) => {
          delete next[productKey];
        });
        return next;
      });
      selectAllProductsWithQuantity();

      if (
        isProductPanelOpen &&
        selectedProduct &&
        deletedProductKeys.has(selectedProduct.productKey)
      ) {
        handleCloseProductPanel();
      }

      toast.success(t('Item(s) Deleted Successfully'));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        t('Unable to delete selected item(s). Please try again.');
      toast.error(errorMessage);
    }
  }, [
    confirm,
    deleteCartItems,
    getSelectedCartProducts,
    handleCloseProductPanel,
    isProductPanelOpen,
    refetchShoppingCart,
    selectAllProductsWithQuantity,
    selectedProduct,
    t,
  ]);

  const updateProductSelection = useCallback(
    (productKey: string, updates: Partial<ShoppingCartProductSelection>) => {
      setProductSelections((prev) => ({
        ...prev,
        [productKey]: {
          ...(prev[productKey] || { selected: false, qty: 0 }),
          ...updates,
        },
      }));
    },
    []
  );

  const fetchProducts = useCallback(async () => refetchShoppingCart(), [refetchShoppingCart]);

  const handleAddToFavorites = useCallback(
    async (folderId: number) => {
      try {
        let selectedProducts: ShoppingCartProduct[] = [];

        if (isProductPanelOpen && selectedProduct) {
          selectedProducts.push(selectedProduct);
        } else {
          selectedProducts = allProducts.filter((product) =>
            Object.entries(visibleProductSelections).some(
              ([productKey, selection]) => selection.selected && productKey === product.productKey
            )
          );
        }

        if (selectedProducts.length === 0) {
          toast.error(t('Please select at least one item to Add to Shopping List'));
          return;
        }

        const folder = folderOptions.find((option) => option.id === folderId);
        const folderName = folder?.name || 'folder';

        const favouriteProduct: FavoriteProduct[] = selectedProducts.map((product) => ({
          productId: product.productId,
          catalogId: product.catalogId,
          categoryId: product.categoryId,
          isCompanySpecifiedItem: product.isFormulary,
          offlineSupplierId: product.supplierId,
        }));

        await addToFavorites({
          favouriteProduct,
          folderId,
        });

        toast.success(t('searchProductsAddedToFavoritesSuccess', { folderName }), {
          autoClose: 3000,
        });

        if (isProductPanelOpen && selectedProduct) {
          await fetchProductDetails();
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to add item(s) to Shopping List. Please try again.';
        toast.error(errorMessage);
      }
    },
    [
      addToFavorites,
      allProducts,
      fetchProductDetails,
      folderOptions,
      isProductPanelOpen,
      selectedProduct,
      t,
      visibleProductSelections,
    ]
  );

  const getSelectedProductCatalogType = useCallback(
    () => (selectedProduct?.isOfflineSupplierItem ? 3 : undefined),
    [selectedProduct]
  );

  usePostMessage({ isDirty: hasUnsavedQuantityChanges });

  const value = useMemo(
    () => ({
      filterSearch,
      handleFilterSearchChange,
      dataState,
      gridDataState: processDataState,
      setDataState,
      onDataStateChange,
      productSelections: visibleProductSelections,
      setProductSelections,
      onSelectionChange,
      onHeaderSelectionChange,
      onItemChange,
      clearFilters,
      hasActiveFilters,
      hasSelectedItems,
      hasSelectedProductsForPo,
      selectedGridTotals,
      allProducts,
      products,
      productsTotal,
      loadingProducts,
      suppliers,
      supplierCardSummaries,
      selectedSupplierIds,
      toggleSupplierSelection,
      handleDeleteSelected,
      isDeletingCartItems,
      saveQuantity,
      hasUnsavedQuantityChanges,
      handleCreatePoForSelected,
      isCreatePoActionInProgress,
      handleReviewContractedAlternatives,
      hasNotRecommendedCartItems,
      handleCloseSwapItemPanel,
      selectedSupplierForInfo,
      openSupplierInfo,
      closeSupplierInfo,
      supplierForInfo,
      isLoadingSupplierInfo,
      isErrorSupplierInfo,
      isSwapItemModalOpen,
      setIsSwapItemModalOpen,
      swapItemData,
      setSwapItemData,
      handleViewProductDetails,
      isProductPanelOpen,
      handleCloseProductPanel,
      selectedProduct,
      setSelectedProduct,
      productDetails,
      loadingProductDetails,
      isFetchingProductDetails,
      updateProductSelection,
      handleAddToFavorites,
      folderOptions,
      isAddingToFavorites,
      isSupplierUser,
      fetchProductDetails,
      fetchProducts,
      getSelectedProductCatalogType,
    }),
    [
      filterSearch,
      handleFilterSearchChange,
      dataState,
      processDataState,
      onDataStateChange,
      visibleProductSelections,
      onSelectionChange,
      onHeaderSelectionChange,
      onItemChange,
      clearFilters,
      hasActiveFilters,
      hasSelectedItems,
      hasSelectedProductsForPo,
      selectedGridTotals,
      allProducts,
      products,
      productsTotal,
      loadingProducts,
      suppliers,
      supplierCardSummaries,
      selectedSupplierIds,
      toggleSupplierSelection,
      handleDeleteSelected,
      isDeletingCartItems,
      saveQuantity,
      hasUnsavedQuantityChanges,
      handleCreatePoForSelected,
      isCreatePoActionInProgress,
      handleReviewContractedAlternatives,
      hasNotRecommendedCartItems,
      handleCloseSwapItemPanel,
      selectedSupplierForInfo,
      openSupplierInfo,
      closeSupplierInfo,
      supplierForInfo,
      isLoadingSupplierInfo,
      isErrorSupplierInfo,
      isSwapItemModalOpen,
      swapItemData,
      handleViewProductDetails,
      isProductPanelOpen,
      handleCloseProductPanel,
      selectedProduct,
      productDetails,
      loadingProductDetails,
      isFetchingProductDetails,
      updateProductSelection,
      handleAddToFavorites,
      folderOptions,
      isAddingToFavorites,
      isSupplierUser,
      fetchProductDetails,
      fetchProducts,
      getSelectedProductCatalogType,
    ]
  );

  return <ShoppingCartContext.Provider value={value}>{children}</ShoppingCartContext.Provider>;
};
